import React, { forwardRef } from "react";
import styles from "./Select.module.scss";
import clsx from "clsx";
import { ValidationInputState } from "../../ValidationInputState";

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  variant?: "default" | "transparent" | "white";
  validationState?: ValidationInputState;
  dataContentPiece: string;
}

function useCombinedRefs(
  ...refs
): React.MutableRefObject<HTMLSelectElement | null> {
  const targetRef = React.useRef<HTMLSelectElement>(null);

  React.useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
}

const SelectBase: React.ForwardRefRenderFunction<
  HTMLSelectElement,
  SelectProps
> = (props, ref) => {
  const {
    validationState = undefined,
    variant = "default",
    className,
    children,
    dataContentPiece,
    ...otherProps
  } = props;
  const innerRef = React.useRef<HTMLSelectElement>(null);
  const combinedRef = useCombinedRefs(ref, innerRef);

  return (
    <select
      ref={combinedRef}
      //onChange={internalOnChange}
      {...otherProps}
      data-value={innerRef?.current?.value}
      aria-invalid={validationState == "error"}
      className={clsx(className, styles.select, {
        [styles.selectDefault]: variant == "default",
        [styles.selectTransparent]: variant == "transparent",
        [styles.selectWhite]: variant == "white",
        [styles.stateInvalid]: validationState == "error"
      })}
      data-content-piece={dataContentPiece}
    >
      {children}
    </select>
  );
};

export const Select = forwardRef(SelectBase);
